/**
 * UC-C (UI-17 to UI-20; Final PRD §35–47, §59) — pure helpers for the Billing review screen. No API access (Jest-tested).
 */
export const REVIEW_CHIPS = [
  ['ALL', 'All'], ['PASS', 'PASS'], ['WARN', 'WARN'], ['BLOCK', 'BLOCK'], ['NON_BILLABLE', 'Non-billable'], ['PROVISIONAL', 'Provisional'], ['UNDECIDED', 'Undecided'], ['NOT_EVALUATED', 'Not evaluated'],
];

export const STAGE_LABEL = { PASS: 'PASS', WARN: 'WARN', BLOCK: 'BLOCK', NON_BILLABLE: 'Non-billable', PROVISIONAL: 'Provisional', UNDECIDED: 'Undecided', NOT_EVALUATED: 'Not evaluated' };
export const STAGE_TONE = { PASS: 'emerald', WARN: 'amber', BLOCK: 'red', NON_BILLABLE: 'slate', PROVISIONAL: 'amber', UNDECIDED: 'slate', NOT_EVALUATED: 'slate' };

export const RECORD_STATE_LABEL = { NONE: 'No record', FINALISED: 'Record finalised', PROVISIONAL: 'Provisional — not invoiceable', INVOICED: 'Invoiced', CLAIMED: 'Claimed', SUPERSEDED: 'Superseded' };
export const recordStateLabel = (rec) => {
  if (!rec) return RECORD_STATE_LABEL.NONE;
  const base = RECORD_STATE_LABEL[rec.state] || rec.state;
  return rec.document?.number ? `${base} · ${rec.document.number}` : base;
};

/** Chip counts from the server `counts` map (the unfiltered set); every chip always listed. */
export const chipCounts = (counts = {}) => REVIEW_CHIPS.map(([key, label]) => ({ key, label, count: counts[key] || 0 }));

/** Client-side mirror of the server stage (used when a row changes after a per-row save, before the list reloads). */
export const reviewStage = (row) => {
  if (row.billing_disposition === 'NON_BILLABLE') return 'NON_BILLABLE';
  const s = row.billing_readiness_result || {};
  if (row.billing_record?.state === 'PROVISIONAL' || (s.warning_codes || []).includes('UNRESOLVED_CLAIM_RULE')) return 'PROVISIONAL';
  if (!row.billing_disposition) return 'UNDECIDED';
  return ['PASS', 'WARN', 'BLOCK'].includes(s.stage) ? s.stage : 'NOT_EVALUATED';
};

const warnCovered = (s) => {
  const cont = s?.warn_continuation;
  return !!cont && (s.warning_codes || []).every((c) => (cont.codes || []).includes(c));
};

/**
 * UI-20 / UAC-07–08: rows that may be sent to invoicing — PASS or continued WARN, disposition BILLABLE, no open record.
 * BLOCK, NON_BILLABLE, PROVISIONAL, UNDECIDED and NOT_EVALUATED are never selected.
 */
export const readyToSend = (rows = []) => rows.filter((r) => {
  if (r.billing_disposition !== 'BILLABLE') return false;
  if (r.billing_record && !['NONE', 'SUPERSEDED'].includes(r.billing_record.state)) return false;
  const stage = reviewStage(r);
  if (stage === 'PASS') return true;
  return stage === 'WARN' && warnCovered(r.billing_readiness_result);
});

/** Sequential per-segment record creation (existing S5 POST /billing/segments/{id}/record); stop at the first error. */
export const sendReadyRows = async (rows, post) => {
  let sent = 0;
  for (const r of rows) {
    try { await post(r); sent += 1; }
    catch (error) { return { sent, failed: { id: r.id, error } }; }
  }
  return { sent, failed: null };
};

/** UO-2 facts: the S5 permissions are reused exactly; a missing permission renders disabled-with-reason, never hidden. */
export const REVIEW_PERMISSION_REASON = {
  'service_allocations.update': 'You need the "Update service allocations" permission to set a billing decision — ask an administrator.',
  'invoices.manage': 'You need the "Manage invoices" permission to continue on WARN, send to invoicing or correct a record — ask an administrator.',
  'timesheets.review': 'You need the "Review timesheets" permission to evaluate readiness.',
};

/** Date range for the UC-A "Next: billing review" link (one participant, one date). */
export const reviewLinkParams = (shift) => ({ tab: 'segments', participant_id: shift?.participant_id || '', start_date: shift?.date || '', end_date: shift?.date || '' });
export const reviewLinkHref = (shift) => `/finance/billing-readiness?${new URLSearchParams(reviewLinkParams(shift)).toString()}`;
